import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const SERVICE_OPTIONS = [
  "Flex Printing", "Banner Printing", "Acrylic Sign Boards", "Vinyl Printing",
  "Offset Printing", "Digital Printing", "Business Cards", "Posters & Brochures",
  "Shop Branding", "Glow Sign Boards", "Custom Branding", "Commercial Printing",
  "Video Editing", "Photo Framing", "T-Shirt & Mug Prints", "Daily Print Services", "Daily Needs",
];

export default function QuoteModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Quote request received! We'll get back within a few hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quote-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] grid place-items-center p-4 bg-brand-ink/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <button
              onClick={onClose}
              data-testid="quote-modal-close"
              className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full bg-gray-100 hover:bg-gray-200"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="grid md:grid-cols-5">
              <div className="md:col-span-2 bg-brand-blue p-8 text-white hidden md:flex md:flex-col md:justify-between">
                <div>
                  <div className="overline text-brand-green">Quick Request</div>
                  <h3 className="mt-3 font-display text-3xl font-extrabold leading-tight">
                    Tell us about your printing project.
                  </h3>
                  <p className="mt-3 text-sm text-white/80 leading-relaxed">
                    Share a few details and we'll send a tailored quote — usually within hours.
                  </p>
                </div>
                <ul className="text-sm text-white/80 space-y-2 mt-8">
                  <li>· Free design consultation</li>
                  <li>· Premium materials</li>
                  <li>· Fast turnaround</li>
                </ul>
              </div>

              <form onSubmit={submit} className="md:col-span-3 p-8 space-y-4">
                <div>
                  <label className="overline text-gray-500">Your Name *</label>
                  <input
                    data-testid="quote-input-name"
                    value={form.name} onChange={update("name")}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                    placeholder="John Doe"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-gray-500">Email *</label>
                    <input
                      data-testid="quote-input-email"
                      type="email" value={form.email} onChange={update("email")}
                      className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                      placeholder="you@email.com"
                    />
                  </div>
                  <div>
                    <label className="overline text-gray-500">Phone *</label>
                    <input
                      data-testid="quote-input-phone"
                      value={form.phone} onChange={update("phone")}
                      className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                      placeholder="+91 ..."
                    />
                  </div>
                </div>
                <div>
                  <label className="overline text-gray-500">Service</label>
                  <select
                    data-testid="quote-input-service"
                    value={form.service} onChange={update("service")}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 bg-white focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                  >
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-gray-500">Project Details *</label>
                  <textarea
                    data-testid="quote-input-message"
                    value={form.message} onChange={update("message")} rows={4}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30 resize-none"
                    placeholder="Tell us about size, quantity, deadline..."
                  />
                </div>
                <button
                  data-testid="quote-submit-btn"
                  type="submit" disabled={loading}
                  className="btn-primary w-full disabled:opacity-60"
                >
                  {loading ? "Sending..." : (<><Send className="h-4 w-4" /> Send Quote Request</>)}
                </button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
